// ============================================================================
// Document upload / list helpers.
//
// Backend: documents.ClinicalDocument (polymorphic via owner_model+owner_id).
// See docs/file_storage.md for the server-side permission matrix.
// ============================================================================

import { api } from './client.js'
import { endpoints } from './endpoints.js'
import { unwrapList } from '../utils/format.js'

/**
 * List clinical documents, optionally scoped to a single owner.
 * @param {object}  opts
 * @param {string=} opts.owner_model  e.g. "clinical_notes.LabOrder"
 * @param {string|number=} opts.owner_id
 * @param {number=} opts.page_size
 */
export async function listDocuments({ owner_model, owner_id, page_size = 50 } = {}) {
  const params = { page_size }
  if (owner_model) params.owner_model = owner_model
  if (owner_id !== undefined && owner_id !== null) params.owner_id = String(owner_id)
  const { data } = await api.get(endpoints.documentList, { params })
  return unwrapList(data)
}

/**
 * Upload a document to an owner row.
 * @param {object} params
 * @param {string} params.owner_model  e.g. "patients.PatientProfile"
 * @param {string|number} params.owner_id
 * @param {File}   params.file         the file blob from an <input type="file">
 * @param {string=} params.kind        free-text kind (e.g. "id_document", "lab_result")
 * @param {string=} params.description
 */
export async function uploadDocument({ owner_model, owner_id, file, kind, description } = {}) {
  const fd = new FormData()
  fd.append('owner_model', owner_model)
  fd.append('owner_id', String(owner_id))
  fd.append('file', file)
  if (kind) fd.append('kind', kind)
  if (description) fd.append('description', description)
  const { data } = await api.post(endpoints.documentList, fd, {
    // axios auto-sets the multipart boundary when Content-Type is omitted
    headers: { 'Content-Type': undefined },
  })
  return data
}

export async function deleteDocument(id) {
  await api.delete(endpoints.documentDetail(id))
}

// ============================================================================
// Opening / downloading a clinical document binary.
//
// Clinical-document files (generated PDFs, uploaded lab/imaging results) are
// served ONLY by the JWT-gated endpoint GET /api/documents/<id>/download/.
// The raw /media/clinical-documents/* path is blocked server-side for PHI
// (see backend/core/media.py), and a plain <a href> / new-tab navigation
// cannot send the bearer token, so it 404s. The file therefore has to be
// pulled in-app through the authenticated axios client (which attaches the
// bearer + auto-refreshes), turned into an object URL, and handed to the
// browser. See DocumentLink.jsx for the UI control.
// ============================================================================

function _documentFilename(doc) {
  if (doc && typeof doc === 'object') {
    return (
      doc.original_filename
      || `${doc.kind || 'document'}${doc.id != null ? `-${doc.id}` : ''}`
      || 'document'
    )
  }
  return `document-${doc}`
}

function _triggerBrowserDownload(objectUrl, filename) {
  const a = document.createElement('a')
  a.href = objectUrl
  a.download = filename || 'document'
  document.body.appendChild(a)
  a.click()
  a.remove()
}

// Revoke an object URL only AFTER the viewer tab is closed, so the PDF
// viewer's own Save/Print button can still re-read the blob. Revoking on a
// short timer (as before) made a later in-viewer "Save" fail — Chrome
// reports a dead blob: URL as "Check internet connection". A 30-minute hard
// cap guards against a leak if the tab is never closed.
function _revokeWhenClosed(win, url) {
  let waited = 0
  const iv = setInterval(() => {
    waited += 2000
    const closed = !win || win.closed
    if (closed || waited > 30 * 60_000) {
      clearInterval(iv)
      URL.revokeObjectURL(url)
    }
  }, 2000)
}

/**
 * Fetch a clinical document's binary through the authenticated client.
 * @param {object|number|string} doc  a ClinicalDocument row (uses doc.id) or a bare id
 * @returns {Promise<Blob>}
 */
export async function fetchDocumentBlob(doc) {
  const id = doc && typeof doc === 'object' ? doc.id : doc
  if (id == null) throw new Error('fetchDocumentBlob: missing document id')
  const { data, headers } = await api.get(endpoints.documentDownload(id), {
    responseType: 'blob',
  })
  if (data instanceof Blob) return data
  const type =
    headers?.['content-type']
    || (doc && typeof doc === 'object' && doc.mime_type)
    || 'application/octet-stream'
  return new Blob([data], { type })
}

/**
 * Open a clinical document in a new browser tab. A blank tab is opened
 * synchronously inside the click gesture (so popup blockers don't kill it),
 * then pointed at the object URL once the authenticated fetch resolves. If
 * the popup was blocked, falls back to a forced download.
 * @param {object|number|string} doc
 */
export async function openDocument(doc) {
  // NOTE: no 'noopener' here — that makes window.open() return null, and we
  // need the handle to redirect the tab after the async fetch.
  const win = typeof window !== 'undefined' ? window.open('', '_blank') : null
  try {
    const blob = await fetchDocumentBlob(doc)
    const url = URL.createObjectURL(blob)
    if (win) {
      win.location = url
      // Keep the blob alive until the viewer tab closes, so the PDF
      // viewer's Save/Print button can re-read it.
      _revokeWhenClosed(win, url)
    } else {
      _triggerBrowserDownload(url, _documentFilename(doc))
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
    }
  } catch (err) {
    if (win) win.close()
    throw err
  }
}

/**
 * Open a clinical document in a new tab and best-effort trigger the print
 * dialog. Same auth path as openDocument. PDF viewers vary in whether they
 * honour an auto-print call, so the tab stays open for a manual Ctrl+P.
 * @param {object|number|string} doc
 */
export async function printDocument(doc) {
  const win = typeof window !== 'undefined' ? window.open('', '_blank') : null
  try {
    const blob = await fetchDocumentBlob(doc)
    const url = URL.createObjectURL(blob)
    if (!win) {
      // Popup blocked — at least get the file to the user.
      _triggerBrowserDownload(url, _documentFilename(doc))
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      return
    }
    win.location = url
    const tryPrint = () => { try { win.print() } catch { /* viewer offers its own */ } }
    win.addEventListener('load', tryPrint)
    setTimeout(tryPrint, 1500)  // belt-and-braces for slow PDF viewers
    _revokeWhenClosed(win, url)  // keep alive for in-viewer Save/Print
  } catch (err) {
    if (win) win.close()
    throw err
  }
}

/**
 * Force-download a clinical document through the authenticated client.
 * @param {object|number|string} doc
 */
export async function downloadDocument(doc) {
  const blob = await fetchDocumentBlob(doc)
  const url = URL.createObjectURL(blob)
  _triggerBrowserDownload(url, _documentFilename(doc))
  setTimeout(() => URL.revokeObjectURL(url), 60_000)
}

/**
 * Fetch every ClinicalDocument that belongs to an encounter or its
 * downstream artefacts — lab orders, imaging orders, the patient
 * profile, and the encounter itself. Returns an object grouped by
 * "appendix bucket" so the clinical summary can render each group with
 * its own heading.
 *
 *   {
 *     encounter: [...],          // docs attached to the encounter row
 *     patient:   [...],          // docs on the patient's profile (ID, insurance, prior records)
 *     labs:      [{ orderId, label, docs: [...] }, ...],
 *     imaging:   [{ orderId, label, docs: [...] }, ...],
 *   }
 *
 * The endpoint scopes by the caller's role — a doctor sees everything
 * they're authorised for; the same call would be safe for a patient
 * viewing their own chart.
 */
export async function listEncounterDocuments(encounter) {
  if (!encounter) return { encounter: [], patient: [], labs: [], imaging: [] }

  const labels = {
    lab: (lo) =>
      (lo.items || []).map((it) => it.test_name).filter(Boolean).join(', ')
      || lo.test_name
      || `Lab order #${lo.id}`,
    imaging: (io) =>
      (io.items || []).map((it) => it.study_name).filter(Boolean).join(', ')
      || io.imaging_type
      || `Imaging order #${io.id}`,
  }

  // Build the parallel fetch list.
  const tasks = []
  tasks.push(
    listDocuments({ owner_model: 'clinical_notes.Encounter', owner_id: encounter.id })
      .then((docs) => ({ key: 'encounter', docs })),
  )
  if (encounter.patient) {
    tasks.push(
      listDocuments({ owner_model: 'patients.PatientProfile', owner_id: encounter.patient })
        .then((docs) => ({ key: 'patient', docs })),
    )
  }
  for (const lo of encounter.lab_orders || []) {
    tasks.push(
      listDocuments({ owner_model: 'clinical_notes.LabOrder', owner_id: lo.id })
        .then((docs) => ({ key: 'lab', orderId: lo.id, label: labels.lab(lo), docs })),
    )
  }
  for (const io of encounter.imaging_orders || []) {
    tasks.push(
      listDocuments({ owner_model: 'clinical_notes.ImagingOrder', owner_id: io.id })
        .then((docs) => ({ key: 'imaging', orderId: io.id, label: labels.imaging(io), docs })),
    )
  }

  // Tolerate per-task failures — one 404 shouldn't kill the whole appendix.
  const settled = await Promise.allSettled(tasks)
  const ok = settled
    .filter((r) => r.status === 'fulfilled')
    .map((r) => r.value)

  const out = { encounter: [], patient: [], labs: [], imaging: [] }
  for (const r of ok) {
    if (r.key === 'encounter') out.encounter = r.docs
    else if (r.key === 'patient') out.patient = r.docs
    else if (r.key === 'lab' && r.docs.length > 0) {
      out.labs.push({ orderId: r.orderId, label: r.label, docs: r.docs })
    } else if (r.key === 'imaging' && r.docs.length > 0) {
      out.imaging.push({ orderId: r.orderId, label: r.label, docs: r.docs })
    }
  }
  return out
}

export function totalDocumentCount(grouped) {
  if (!grouped) return 0
  let n = (grouped.encounter || []).length + (grouped.patient || []).length
  for (const g of grouped.labs || []) n += g.docs.length
  for (const g of grouped.imaging || []) n += g.docs.length
  return n
}

// ============================================================================
// Generated documents (sick certs, prescriptions, lab/imaging requests,
// referral letters, invoices). Backend: POST /api/documents/generate/.
//
// On success returns a ClinicalDocument row with `file_url` set — the
// caller typically opens it in a new tab so the browser handles the PDF
// preview/download natively.
// ============================================================================

/**
 * Document types the backend service supports. Keep in sync with
 * documents/services/generators.py:_REGISTRY. Order here is the order
 * shown in the UI button row.
 */
export const GENERATED_DOCUMENT_TYPES = [
  { kind: 'medical_report',    label: 'Medical Report',   short: 'Report',     needsForm: true  },
  { kind: 'sick_certificate',  label: 'Sick Certificate', short: 'Sick cert',  needsForm: true  },
  { kind: 'prescription',      label: 'Prescription',     short: 'Rx PDF',     needsForm: false },
  { kind: 'lab_request',       label: 'Lab Request',      short: 'Lab',        needsForm: false },
  { kind: 'imaging_request',   label: 'Imaging Request',  short: 'Imaging',    needsForm: false },
  { kind: 'referral_letter',   label: 'Referral Letter',  short: 'Referral',   needsForm: false },
  { kind: 'invoice',           label: 'Invoice / Receipt',short: 'Invoice',    needsForm: true  },
]

/**
 * Generate a clinical document.
 * @param {object} args
 * @param {string} args.document_type  one of GENERATED_DOCUMENT_TYPES[].kind
 * @param {number} args.encounter_id
 * @param {object} [args.payload]      doc-type-specific extras
 *                                     (days_off for sick cert; items for invoice; etc.)
 * @returns {Promise<object>}          ClinicalDocument row { id, file_url, kind, ... }
 */
export async function generateDocument({ document_type, encounter_id, payload = {} } = {}) {
  const { data } = await api.post(endpoints.documentGenerate, {
    document_type,
    encounter_id: Number(encounter_id),
    payload,
  })
  return data
}

/**
 * Fetch a pre-filled invoice payload for an encounter — every billable
 * artefact (consultation fee + each Rx item × indicative price + each
 * lab test + each imaging study) rolled into a list the InvoiceModal
 * can drop straight into its row state. Saves the doctor from typing
 * line-by-line; they review and adjust before clicking Generate.
 *
 * Backend: GET /api/documents/draft-invoice/?encounter_id=<id>
 *
 * Returns: { items, currency, subtotal, patient_name, encounter_id,
 *            bill_to_org_name, bill_to_org_id, notes }
 */
export async function getDraftInvoice(encounterId) {
  const { data } = await api.get(`${endpoints.documentList}draft-invoice/`, {
    params: { encounter_id: Number(encounterId) },
  })
  return data
}

/**
 * List generated documents for an encounter (one flat list, newest
 * first). Used by the panel to show the doctor what's already been
 * issued so they can re-open / re-print without re-generating.
 */
export async function listGeneratedDocuments(encounterId) {
  if (!encounterId) return []
  const { data } = await api.get(endpoints.documentList, {
    params: { encounter: encounterId, generated_only: true, page_size: 100 },
  })
  return unwrapList(data)
}

/**
 * Email a generated document as a PDF attachment.
 * @param {object} args
 * @param {number} args.id        — ClinicalDocument id
 * @param {string} [args.to]      — recipient (defaults to patient email server-side)
 * @param {string[]|string} [args.cc]
 * @param {string} [args.subject]
 * @param {string} [args.message]
 * @returns {Promise<{sent_to, subject, document_id, delivered}>}
 */
export async function emailDocument({ id, to, cc, subject, message } = {}) {
  const body = {}
  if (to) body.to = to
  if (cc) body.cc = cc
  if (subject) body.subject = subject
  if (message) body.message = message
  const { data } = await api.post(endpoints.documentEmail(id), body)
  return data
}
